import { ExternalLink } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { VideoEmbed } from '@/components/ui/VideoEmbed';
import { hasExtraDetails, hasRichTextContent, richTextToPlain } from '@/lib/alertHeroStyle';
import type { AccountAlert } from '@/services/accountAlerts.service';

interface AlertDetailModalProps {
  alert: AccountAlert | null;
  onClose: () => void;
}

export function AlertDetailModal({ alert, onClose }: AlertDetailModalProps) {
  if (!alert) return null;

  // Widen the modal when there's a video so the player has room.
  const size = alert.videoURL ? 'xl' : 'lg';

  return (
    <Modal isOpen={!!alert} onClose={onClose} title={richTextToPlain(alert.alertTitle)} size={size}>
      <div className="space-y-5">
        {alert.videoURL && <VideoEmbed url={alert.videoURL} title={richTextToPlain(alert.alertTitle)} />}

        {alert.alertPhoto && (
          <div className="flex justify-center">
            {alert.alertURL ? (
              <a href={alert.alertURL} target="_blank" rel="noopener noreferrer">
                <img
                  src={alert.alertPhoto}
                  alt={richTextToPlain(alert.alertTitle)}
                  className="max-w-full max-h-64 rounded-lg object-contain hover:opacity-90 transition-opacity"
                />
              </a>
            ) : (
              <img
                src={alert.alertPhoto}
                alt={richTextToPlain(alert.alertTitle)}
                className="max-w-full max-h-64 rounded-lg object-contain"
              />
            )}
          </div>
        )}

        {alert.alertURL && (
          <a
            href={alert.alertURL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Open Link
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}

        {hasRichTextContent(alert.alertContent) && (
          <div className="border-t pt-4">
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: alert.alertContent }}
            />
          </div>
        )}

        {hasExtraDetails(alert.alertContent, alert.moreDetails) && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">More Details</h4>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: alert.moreDetails }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
